import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Mail, Send } from "lucide-react";
import { toast } from "sonner";

export default function FacilityEmailDialog({ facility, open, onOpenChange }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);

  useEffect(() => {
    if (!open || !facility) return;
    setSubject(`Follow-up regarding ${facility.name}`);
    setBody("");
    api.get(`/facilities/${facility.facility_id}/email-log`).then(({ data }) => setLog(data)).catch(() => setLog([]));
  }, [open, facility]);

  const send = async () => {
    if (!body.trim()) { toast.error("Write a message"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/facilities/${facility.facility_id}/send-email`, { subject, body });
      toast.success(`Sent to ${data.recipient}`);
      const { data: fresh } = await api.get(`/facilities/${facility.facility_id}/email-log`);
      setLog(fresh);
      setBody("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  if (!facility) return null;
  const hasEmail = !!facility.admin_contact_email;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5" /> Email facility contact</DialogTitle>
        </DialogHeader>
        {!hasEmail ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            This facility has no admin contact email on file. Add one on the facility record first.
          </div>
        ) : (
          <>
            <div className="text-xs text-slate-500 font-mono-plex">
              To: <span className="text-slate-900">{facility.admin_contact_name || "Facility admin"}</span> · <span className="text-slate-900">{facility.admin_contact_email}</span>
            </div>
            <div><Label>Subject line (short)</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject" maxLength={80} placeholder="Follow-up regarding …" />
              <div className="text-[10px] text-slate-400 font-mono-plex mt-1">Prefixed with [Ace Behavioral Health].</div>
            </div>
            <div><Label>Message</Label>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} data-testid="email-body" placeholder={`Hi ${facility.admin_contact_name || "there"},\n\n`} />
            </div>
            <div className="text-xs text-slate-500">Replies go directly to your inbox (Reply-To is set to your email).</div>

            {log.length > 0 && (
              <Card className="mt-3 p-4 border-slate-200 shadow-none">
                <div className="text-[10px] uppercase tracking-widest font-mono-plex text-slate-500 mb-2">Recent sends</div>
                <div className="space-y-2">
                  {log.slice(0, 4).map((l) => (
                    <div key={l.log_id} className="text-xs text-slate-600 flex justify-between gap-2">
                      <span className="truncate">{l.subject}</span>
                      <span className="font-mono-plex text-slate-400 shrink-0">{(l.created_at || "").slice(0, 16).replace("T", " ")} · {l.sent_by_name}</span>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={send} disabled={busy || !hasEmail || !body.trim()} data-testid="email-send" className="bg-slate-900 hover:bg-slate-800">
            <Send className="w-4 h-4 mr-1" />{busy ? "Sending…" : "Send email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
